import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { cashUp, formatMoney, moneyFromCents } from '@wodi/domain';
import type { ShiftView } from '@wodi/shared';
import {
  Button,
  EmptyState,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  PrintHeader,
  PrintLines,
  PrintSheet,
  printSheet,
  TextArea,
  useShortcut,
} from '@wodi/ui';
import { Fragment, useEffect, useRef, useState } from 'react';

import { billingKeys, closeShift, currentShiftQuery } from '../api/billing.ts';
import { sessionQuery } from '../api/session.ts';
import styles from '../billing/billing.module.css';
import { cents, METHOD_LABELS, money, type PaymentMethodKey } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, ShortcutHint } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

const METHODS: PaymentMethodKey[] = ['cash', 'mpesa', 'card', 'bank_transfer'];

/**
 * The shift summary and cash-up (cashier-and-payments.md §5): what is expected by method,
 * the cash counted, the variance in words and its reason. The summary prints on close.
 */
export function CashierShiftPage() {
  const shift = useQuery(currentShiftQuery);
  const [closed, setClosed] = useState<ShiftView | null>(null);
  return (
    <div className={styles.page}>
      <Link to="/cashier" search={{}}>
        Back to the cashier
      </Link>
      <PageHeader title="Shift" />
      {shift.isPending ? <LoadingBlock label="Loading your shift" rows={3} /> : null}
      {shift.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void shift.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(shift.error, "Your shift couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {closed !== null ? (
        <ShiftSummary shift={closed} autoPrint />
      ) : shift.isSuccess && shift.data.shift === null ? (
        <EmptyState>You have no shift open. Open one from the cashier screen.</EmptyState>
      ) : shift.isSuccess && shift.data.shift !== null ? (
        <CloseShift shift={shift.data.shift} onClosed={setClosed} />
      ) : null}
    </div>
  );
}

function ExpectedTable({ shift }: { shift: ShiftView }) {
  return (
    <dl className={screen.amounts}>
      <dt>Opening float</dt>
      <dd>{money(shift.openingFloat)}</dd>
      {METHODS.map((m) => (
        <Fragment key={m}>
          <dt>{`${METHOD_LABELS[m]} received`}</dt>
          <dd>{money(shift.received[m])}</dd>
        </Fragment>
      ))}
      <dt>Cash refunded</dt>
      <dd>{money(shift.refunded.cash)}</dd>
      <dt data-total="">Cash expected in the drawer</dt>
      <dd data-total="">{money(shift.expectedCash)}</dd>
    </dl>
  );
}

function CloseShift({ shift, onClosed }: { shift: ShiftView; onClosed: (s: ShiftView) => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [counted, setCounted] = useState<bigint | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const expected = moneyFromCents(cents(shift.expectedCash));
  const preview =
    counted === null ? null : cashUp({ expected, counted: moneyFromCents(counted), reason });
  const variance = counted === null ? null : counted - expected.cents;
  const varianceWords =
    variance === null
      ? null
      : variance === 0n
        ? 'The drawer balances.'
        : `${formatMoney(moneyFromCents(variance < 0n ? -variance : variance))} ${variance < 0n ? 'short' : 'over'}.`;

  const submit = useMutation({
    mutationFn: () =>
      closeShift(shift.id, {
        countedCashCents: (counted ?? 0n).toString(),
        ...(reason.trim() === '' ? {} : { varianceReason: reason.trim() }),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({ title: 'Shift closed', description: result.number });
      onClosed(result);
    },
  });
  const go = () => {
    setTried(true);
    if (counted !== null && preview?.ok === true) submit.mutate();
  };
  useShortcut('mod+enter', go, { allowInFields: true, description: 'Close shift' });

  return (
    <section className={`${styles.section} ${screen.panel}`} aria-labelledby="close-heading">
      <h2 id="close-heading" className={styles.sectionHeading}>
        <span>Shift </span>
        <span className="wodi-id">{shift.number}</span>
        <span>{`, open since ${formatInstant(shift.openedAt)}`}</span>
      </h2>
      <p>
        {shift.paymentCount === 1
          ? '1 payment taken.'
          : `${String(shift.paymentCount)} payments taken.`}
      </p>
      <ExpectedTable shift={shift} />
      <div className={styles.form}>
        <div className={styles.narrowField}>
          <MoneyField
            label="Cash counted"
            description="Count the drawer, including the float."
            value={counted}
            onChange={(value) => {
              setCounted(value);
            }}
            {...(tried && counted === null ? { errorMessage: 'Enter the cash counted.' } : {})}
          />
        </div>
        {varianceWords === null ? null : <p className={styles.summary}>{varianceWords}</p>}
        {variance !== null && variance !== 0n ? (
          <TextArea
            label="Why is it different?"
            description="Required. It appears on the owner's day summary."
            value={reason}
            onChange={setReason}
            {...(tried && reason.trim() === ''
              ? { errorMessage: 'Say why the cash is different.' }
              : {})}
          />
        ) : null}
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The shift wasn't closed. Try again.")}
          </Notice>
        ) : null}
        <p>A closed shift can't be reopened.</p>
        <div className={styles.toolbar}>
          <Button
            variant="primary"
            onPress={go}
            isPending={submit.isPending}
            pendingLabel="Closing"
            isDisabled={!online}
            shortcut="mod+enter"
          >
            Close shift
          </Button>
          <ShortcutHint combo="mod+enter" action="close the shift" />
        </div>
      </div>
    </section>
  );
}

function ShiftSummary({ shift, autoPrint }: { shift: ShiftView; autoPrint: boolean }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const sheet = useRef<HTMLElement>(null);
  const printed = useRef(false);
  const print = () => {
    if (sheet.current !== null) printSheet(sheet.current);
  };
  // The summary prints once, as the shift closes (cashier-and-payments.md §5.3).
  useEffect(() => {
    if (autoPrint && !printed.current && sheet.current !== null) {
      printed.current = true;
      printSheet(sheet.current);
    }
  }, [autoPrint]);
  return (
    <section className={styles.receiptPreview}>
      <Button onPress={print}>Print shift summary</Button>
      <div>
        <PrintSheet paper="receipt-80mm" label="Shift summary" ref={sheet}>
          <PrintHeader facility={session?.facility.name ?? ''} title="Shift summary" />
          <PrintLines
            items={[
              { label: 'Shift no.', value: shift.number, identifier: true },
              { label: 'Cashier', value: shift.cashier.name },
              { label: 'Opened', value: formatInstant(shift.openedAt) },
              {
                label: 'Closed',
                value: shift.closedAt === null ? '' : formatInstant(shift.closedAt),
              },
              { label: 'Opening float', value: money(shift.openingFloat) },
              ...METHODS.map((m) => ({ label: METHOD_LABELS[m], value: money(shift.received[m]) })),
              { label: 'Cash expected', value: money(shift.expectedCash) },
              {
                label: 'Cash counted',
                value: shift.countedCash === null ? '' : money(shift.countedCash),
              },
              {
                label: 'Variance',
                value: shift.variance === null ? '' : money(shift.variance),
              },
              ...(shift.varianceReason === null
                ? []
                : [{ label: 'Reason', value: shift.varianceReason }]),
            ]}
          />
        </PrintSheet>
      </div>
    </section>
  );
}
